<script setup>
import { computed, ref, toRefs } from 'vue'

import SignAction from '@/src/javascripts/document/SignAction.vue'
import ReferralAction from '@/src/javascripts/document/ReferralAction.vue'
import BackButton from '@/src/vueComponent/BackButton.vue'

import mySwal from '@/src/javascripts/plugins/mySwal.js'

const props = defineProps({
  token: {
    type: String
  },
  current_user: {
    type: Object,
    default: () => ({})
  },
  documentData: {
    type: Object,
    default: () => ({})
  },
  unitList: {
    type: Array,
    default: () => []
  }
})

const { token, documentData, unitList } = toRefs(props)

const editDocumentData = ref(documentData.value)

const totalReportFileList = computed(() => {
  return editDocumentData.value.reports.reduce((acc, report) => {
    return acc.concat(report.file_infos)
  }, [])
})

const deleteReportFileList = ref([])
const deleteReportFile = (reportIndex, reportID, fileIndex) => {
  deleteReportFileList.value = [...deleteReportFileList.value, reportID]

  editDocumentData.value.reports[reportIndex].file_infos = [
    ...editDocumentData.value.reports[reportIndex].file_infos.slice(0, fileIndex),
    ...editDocumentData.value.reports[reportIndex].file_infos.slice(fileIndex + 1)
  ]
}

const openReferralList = ref([])
const toggleReferral = (stepId) => {
  if (openReferralList.value.includes(stepId)) {
    openReferralList.value = openReferralList.value.filter((id) => id !== stepId)
  } else {
    openReferralList.value.push(stepId)
  }
}

const transformedCustomerCategory = computed(() => {
  if (editDocumentData.value.customer?.category === 'strand') {
    return '絞線'
  }
  if (editDocumentData.value.customer?.category === 'other') {
    return '其他'
  }
  return ''
})

const transformStepAction = (action) => {
  const actionMap = I18n.t('sign_action')
  return actionMap[action] || '--'
}

const transformReferralAction = (action) => {
  const actionMap = I18n.t('referral_action')
  return actionMap[action] || '--'
}

const cancelDocument = (documentFromData) => {
  if (documentFromData.sign_action === 'cancel') {
    mySwal
      .confirm({
        title: '確定要取消公文嗎？',
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        customClass: {
          confirmButton: 'btn m-[0.3125em] bg-scp-royal-blue text-white text-lg hover:bg-blue-800',
          cancelButton: 'btn m-[0.3125em] text-lg btn-ghost'
        },
        buttonsStyling: false
      })
      .then((result) => {
        if (result) {
          submitForm(documentFromData)
        }
      })
  }
}

const submitForm = (documentFromData) => {
  if (!['forward', 'branch', 'referral'].includes(documentFromData.sign_action)) {
    const { target, ...rest } = documentFromData
    documentFromData = rest
  }

  documentFromData = {
    ...documentFromData,
    remove_file_ids: deleteReportFileList.value
  }

  const formData = new FormData()
  for (const [key, value] of Object.entries(documentFromData)) {
    if (Array.isArray(value)) {
      value.forEach((multipleElement, index) => {
        if (multipleElement instanceof File && key === 'upload_files') {
          formData.append(`upload_files[${index}]`, multipleElement)
        } else if (key === 'target') {
          formData.append(`${key}[${index}][signer_id]`, multipleElement.signer_id)
          formData.append(`${key}[${index}][step_name]`, multipleElement.step_name)
        } else {
          formData.append(`${key}[]`, multipleElement)
        }
      })
    } else {
      formData.append(key, value)
    }
  }

  $.ajax({
    url: `/documents/${editDocumentData.value.id}/sign`,
    type: 'POST',
    dataType: 'json',
    headers: { 'X-CSRF-Token': token.value },
    authenticity_token: true,
    processData: false,
    contentType: false,
    enctype: 'multipart/form-data',
    data: formData,
    success: async (response) => {
      if (response.state === 0) {
        mySwal
          .success({
            title: response.message
          })
          .then(() => {
            $(document).off('turbo:before-visit')
            Turbo.visit('/documents')
          })
      }
    },
    error: (error) => {
      console.log('error', error)
      if (error.responseJSON.state === -1) {
        mySwal.error({
          title: error.responseJSON.message.join('<br />')
        })
      }
    }
  })
}
</script>
<template>
  <div>
    <h3 class="py-2.5 text-2xl font-bold">
      內部簽核單 {{ editDocumentData?.document_index ?? '' }}
    </h3>
    <div class="border-ag-indigo-3 rounded-md border">
      <table class="w3-table w3-bordered border-0">
        <tbody>
          <tr>
            <th class="whitespace-no-wrap">發起人</th>
            <td>
              {{ editDocumentData.applicant?.company ?? '' }}
              {{ editDocumentData.applicant?.unit ?? '' }}
              {{ editDocumentData.applicant?.name ?? '' }}
            </td>
          </tr>
          <tr>
            <th class="whitespace-no-wrap">簽核編號</th>
            <td>
              {{ editDocumentData?.document_index ?? '' }}
            </td>
          </tr>
          <tr>
            <th class="whitespace-no-wrap">客戶</th>
            <td>{{ editDocumentData.customer?.name ?? '' }}</td>
          </tr>
          <tr>
            <th class="whitespace-no-wrap">申請單號</th>
            <td>
              <a
                :href="`/quality_feedbacks/${editDocumentData.quality_feedback_id}`"
                class="text-scp-royal-blue underline"
                target="_blank"
              >
                {{ editDocumentData?.feedback_code ?? '' }}
              </a>
            </td>
          </tr>
          <tr>
            <th class="whitespace-no-wrap">製造資料 - {{ transformedCustomerCategory }}</th>
            <td>{{ editDocumentData?.procedure_name ?? '' }}</td>
          </tr>
          <tr>
            <th class="whitespace-no-wrap">資料預覽</th>
            <td>
              <table class="w-full">
                <thead class="font-bold">
                  <tr>
                    <td>提供者</td>
                    <td>提供日期</td>
                    <td>資料</td>
                  </tr>
                </thead>
                <tbody v-if="editDocumentData.reports.length !== 0">
                  <tr v-for="(report, reportIndex) in editDocumentData.reports" :key="report.id">
                    <td>{{ report.provider.name }}</td>
                    <td>{{ report.provided_at }}</td>
                    <td>
                      <template v-if="report.category === 'data'">
                        <a
                          :href="`/reports/${report.id}`"
                          class="block text-scp-royal-blue underline"
                          target="_blank"
                        >
                          {{ editDocumentData.procedure_name }}
                        </a>
                      </template>
                      <template v-if="report.file_infos.length !== 0">
                        <p
                          v-for="(fileInfo, fileIndex) in report.file_infos"
                          :key="fileInfo.id"
                          class="mb-2 flex items-center gap-2 last-of-type:mb-0"
                        >
                          <a
                            :href="fileInfo.url"
                            download
                            target="_blank"
                            class="block text-scp-royal-blue underline"
                          >
                            {{ fileInfo.name }}
                          </a>
                          <span
                            v-if="Boolean(editDocumentData.is_current_signer)"
                            class="cursor-pointer text-red-400"
                            @click.prevent="deleteReportFile(reportIndex, fileInfo.id, fileIndex)"
                          >
                            <i class="icon-trash text-lg"></i>
                          </span>
                        </p>
                      </template>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="3">無資料</td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
          <tr>
            <th>簽核流程</th>
            <td>
              <ul class="w-full">
                <li class="detail-header-li">
                  <span>簽核單位</span>
                  <span>簽核人員</span>
                  <span>簽核動作</span>
                  <span>時間</span>
                  <span>備註</span>
                </li>
                <li class="detail-body-li" v-for="step in editDocumentData.steps" :key="step.id">
                  <div class="md-block md:col-span-1">
                    <span class="md-title">簽核單位</span>
                    <span>{{ step.signer.unit }} </span>
                  </div>
                  <div class="md-block md:col-span-1">
                    <span class="md-title">簽核人員</span>
                    <span>
                      {{ step.signer.name }}
                    </span>
                  </div>
                  <div class="md-block md:col-span-1">
                    <span class="md-title">簽核動作</span>
                    <span
                      v-if="step.action === 'referral'"
                      class="cursor-pointer text-scp-royal-blue underline"
                      @click.prevent="toggleReferral(step.id)"
                    >
                      {{ transformStepAction(step.action) }}
                    </span>
                    <span v-else>
                      {{ transformStepAction(step.action) }}
                    </span>
                  </div>
                  <div class="md-block md:col-span-1">
                    <span class="md-title">時間</span>
                    <span>{{ step.signed_at }}</span>
                  </div>
                  <div class="md-block md:col-span-2">
                    <span class="md-title">備註</span>
                    <span>
                      <pre class="whitespace-pre-wrap">{{ step?.suggestion ?? '' }}</pre>
                    </span>
                  </div>
                  <div
                    v-if="openReferralList.includes(step.id)"
                    class="detail-referral-li animated fadeIn col-span-5 md:col-span-1"
                  >
                    <div
                      v-for="referral in editDocumentData.referral_steps"
                      :key="referral.id"
                      class="grid grid-cols-5"
                    >
                      <template v-if="referral.step === step.step">
                        <div class="md-block md:col-span-1">
                          <span class="md-title">部門</span>
                          <span>{{ referral.signer.unit }}</span>
                        </div>
                        <div class="md-block md:col-span-1">
                          <span class="md-title">人員</span>
                          <span>{{ referral.signer.name }}</span>
                        </div>
                        <div class="md-block md:col-span-1">
                          <span class="md-title">動作</span>
                          <span>{{ transformReferralAction(referral.action) }}</span>
                        </div>
                        <div class="md-block md:col-span-1">
                          <span class="md-title">時間</span>
                          <span>{{ referral?.signed_at ?? '--' }}</span>
                        </div>
                        <div class="md-block md:col-span-2">
                          <span class="md-title">備註</span>
                          <span>{{ referral.suggestion }}</span>
                        </div>
                      </template>
                    </div>
                  </div>
                </li>
              </ul>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div>
      <SignAction
        v-if="Boolean(editDocumentData.is_current_signer)"
        :currentUser="current_user"
        :documentShowInfo="editDocumentData"
        :unitList="unitList"
        :totalReportFileList="totalReportFileList"
        @submitForm="submitForm"
      />
      <ReferralAction
        v-if="
          !Boolean(editDocumentData.is_current_signer) &&
          Boolean(editDocumentData.is_unread_referred_user)
        "
        @submitForm="submitForm"
      />
    </div>
    <div class="flex items-center justify-end gap-3 py-3">
      <button
        v-if="
          current_user.id === editDocumentData.applicant.id &&
          editDocumentData.state === 'approving' &&
          !Boolean(editDocumentData.is_current_signer) &&
          !Boolean(editDocumentData.is_unread_referred_user)
        "
        class="btn bg-red-500 text-xl text-white"
        @click.prevent="cancelDocument({ sign_action: 'cancel' })"
      >
        取消公文
      </button>
      <BackButton
        v-if="
          !Boolean(editDocumentData.is_current_signer) &&
          !Boolean(editDocumentData.is_unread_referred_user)
        "
        :hasForm="false"
        :returnPath="'/documents'"
      />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.w3-table {
  tr {
    th {
      width: 230px;
    }
  }
}

.manufacturing-data {
  label {
    font-weight: normal !important;
    font-size: 1rem !important;
  }
}

.detail-header-li {
  @apply grid grid-cols-5 pb-1 font-bold border-b border-solid  leading-8 md:hidden;
}
.detail-body-li {
  @apply border-b border-solid  grid grid-cols-5 leading-8 align-middle py-1 md:pb-4 md:grid-cols-2 md:leading-normal last:border-b-0;
}
.detail-referral-li {
  @apply border-b border-solid leading-8 bg-[#e5eeff] text-scp-royal-blue align-middle py-1 md:pb-4 md:grid-cols-2 md:leading-normal last:border-b-0;
}
.md-block {
  @apply md:inline-flex md:leading-8;
  .md-title {
    @apply hidden font-bold mr-1 md:inline md:flex-none;
  }
}
</style>
